import { MimeType } from '../../../common/media/mimeConstants';
import { getMimeTypeFromImageUrl, isPermissionCheckedAppMediaUrl } from './imageUtils';
import platformSingleton from '../../platform/platformSingleton';
import { isPlatformCapacitor } from '../../platform/utils/platformDetailsUtils';
import { getCurrentUserToken, getHasRememberedBetterAuthSessionUser } from '../../auth/authService';
import { getAuthenticatedHeaders, shouldFetchWithAuthentication } from '../../auth/authUtils';

/**
 * Fetch image bytes through the browser fetch API when standard web CORS/auth handling is available.
 */
const fetchImageResponse = (
    url: string,
    token?: string,
    cache?: RequestCache,
    credentials?: RequestCredentials,
): Promise<Response> => {
    const init: RequestInit = { headers: getAuthenticatedHeaders(token) };
    if (cache) init.cache = cache;
    if (credentials) init.credentials = credentials;

    return fetch(url, init);
};

/**
 * Try public image URLs without auth first, except for app-hosted v2 media URLs that require auth for
 * permission checks.
 */
const fetchImageBlobUsingBrowserFetch = async (url: string, cache?: RequestCache): Promise<Blob> => {
    const token = getCurrentUserToken() ?? undefined;
    const isMilanoteMedia = shouldFetchWithAuthentication(url);
    // A Better Auth cookie session has no legacy token; it authenticates via the session cookie,
    // which cross-origin fetches only attach when `credentials: 'include'` is set. Treat such a
    // session as able to authenticate milanote media so cookie users aren't stuck as "anonymous"
    // and denied the authenticated retry below.
    const hasCookieSession = !token && getHasRememberedBetterAuthSessionUser();
    const canSendAuthentication = isMilanoteMedia && (!!token || hasCookieSession);
    // App-hosted v2 media is permission-checked, so its first request must already be authenticated.
    // CDN-hosted media stays public-first to avoid the CORS regression fixed in PR #20897.
    const shouldSendAuthenticationImmediately = isMilanoteMedia && isPermissionCheckedAppMediaUrl(url);
    // Send the session cookie only when the cookie IS the credential (no legacy token). Never send it
    // alongside a bearer token — credentials + a token force a stricter credentialed-CORS response
    // (exact origin + Allow-Credentials) that the media CDN does not return for token-authenticated
    // requests. Legacy users authenticate with the bearer header instead.
    const useCookieCredentials = isMilanoteMedia && hasCookieSession;
    const response = await fetchImageResponse(
        url,
        shouldSendAuthenticationImmediately ? token : undefined,
        cache,
        shouldSendAuthenticationImmediately && useCookieCredentials ? 'include' : undefined,
    );

    if (response.ok) return response.blob();
    if (shouldSendAuthenticationImmediately || !canSendAuthentication) {
        throw new Error(`Failed to fetch image (${response.status})`);
    }

    // Public-first milanote media that turned out to need auth: retry authenticated. Legacy users
    // send the bearer token; a Better Auth cookie session (no token) sends the session cookie via
    // credentials — the anonymous attempt above 403s on a permission-checked image the URL didn't
    // route through the immediate app-hosted-v2 auth path (e.g. a CDN-hosted or non-v2 source URL).
    const authenticatedResponse = await fetchImageResponse(
        url,
        token,
        cache,
        useCookieCredentials ? 'include' : undefined,
    );

    if (authenticatedResponse.ok) return authenticatedResponse.blob();

    throw new Error(`Failed to fetch image (${authenticatedResponse.status})`);
};

/**
 * `response.blob()` derives the blob's MIME type from the response Content-Type alone, which can be
 * missing or generic (e.g. `application/octet-stream`). Prefer the caller-supplied imageType, then
 * the response type, then the type inferred from the URL, so downstream editor / background-removal
 * processing always receives a usable image type.
 */
const withResolvedImageType = (blob: Blob, url: string, imageType?: string): Blob => {
    const resolvedType = imageType || blob.type || getMimeTypeFromImageUrl(url, MimeType.JPEG);
    return blob.type === resolvedType ? blob : new Blob([blob], { type: resolvedType });
};

/**
 * Download image bytes for editing or background removal.
 */
export const fetchImageBlobFromUrl = async (url: string, imageType?: string): Promise<Blob> => {
    const fetchBlob = async (): Promise<Blob> => {
        try {
            return await fetchImageBlobUsingBrowserFetch(url);
        } catch (error) {
            // On web a fetch-level failure (TypeError) is usually a poisoned browser cache: an earlier
            // <img> load of the same URL without an Origin header can be served — missing its
            // Access-Control-Allow-Origin header — to this CORS fetch despite `Vary: Origin`, so retry
            // once bypassing the HTTP cache. On native there is no such cache path, so surface the
            // failure rather than masking a genuine image-loading problem behind a native-HTTP fallback.
            if (!(error instanceof TypeError) || isPlatformCapacitor(platformSingleton)) throw error;
            return fetchImageBlobUsingBrowserFetch(url, 'reload');
        }
    };

    return withResolvedImageType(await fetchBlob(), url, imageType);
};

/**
 * Download image bytes and expose them as a local object URL, for editor / background-removal flows
 * that need a same-origin blob URL.
 */
export const fetchImageObjectUrl = async (
    imageUrl: string,
    imageType?: string,
): Promise<{ objectUrl: string }> => {
    const blob = await fetchImageBlobFromUrl(imageUrl, imageType);
    return { objectUrl: URL.createObjectURL(blob) };
};
